import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { loginUser } from "../../service/authService";
import { toast } from "react-toastify";
import HttpRequest from "../../functions/HttpRequest";
import PinInput from "../../components/PinInput";
import SwalNotification from "../../functions/SwalNotification";
import is_null from "../../functions/is_null";

const LoginForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [accessCode, setAccessCode] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission

    try {
      setLoading(true);
      console.log('=== Login Attempt Started ===');
      
      if (is_null(accessCode)) {
        setLoading(false);
        toast.error("Please enter your 6-digit access code");
        return; // Early return, don't throw
      }

      if (accessCode.length !== 6) {
        setLoading(false);
        toast.error("Access code must be exactly 6 digits");
        return; // Early return, don't throw
      }

      // Make login request to backend
      console.log('Making login request to:', '/nuc_accreditation/panel/auth');
      console.log('Access code:', accessCode);
      
      const req = await HttpRequest(`/nuc_accreditation/panel/auth`, {
        body: JSON.stringify({
          accessCode: accessCode.trim(),
        }),
        method: "POST",
        headers: { "Content-type": "application/json" },
      });

      console.log('Login response:', req);
      console.log('Response type:', req?.type);
      console.log('Response has token:', !!req?.token);
      console.log('Response has user:', !!req?.user);

      // Check if response indicates success
      if (req?.type === 'success' && req?.token) {
        const { token, user, programmes, accessType, info } = req;

        console.log('✅ Login successful!');
        console.log('Access Type:', accessType);
        console.log('User:', user);
        console.log('Programmes:', programmes);
        console.log('Info:', info);

        let programmesToStore = [];

        // Handle different access types
        if (accessType === 'panel') {
          // Panel access - use programmes array from response
          programmesToStore = programmes || [];
        } else if (accessType === 'accreditation' || accessType === 'panelMember') {
          // Direct accreditation or panel member - create single programme from info
          if (info && info.programmeID) {
            programmesToStore = [{
              id: info._id,
              _id: info._id,
              programmeId: info.programmeID._id,
              programmeName: info.programmeID.name,
              institutionName: info.programmeID.university?.name || 'Unknown Institution',
              accreditationData: {
                stage: info.stage,
                status: info.status,
                startDate: info.startDate,
                endDate: info.endDate,
                expire: info.expire
              }
            }];
          }
        }

        console.log('Programmes to store:', programmesToStore);

        // Store authentication data
        const isAuthenticated = await loginUser({
          token,
          user: user || {},
          programmes: programmesToStore
        });
        console.log({
          token,
          user: user || {},
          programmes: programmesToStore,
          isAuthenticated
        })

        if (isAuthenticated) {
          SwalNotification("success", "Login Successful", "Success");

          // Always redirect to settings page for programme selection
          // This prevents issues with dashboard loading before programme is properly selected
          console.log(`Found ${programmesToStore.length} programme(s), redirecting to /settings`);

          setTimeout(() => {
            navigate("/settings");
          }, 800);
        } else {
          console.error('❌ Failed to store authentication data');
          toast.error("Login Failed - Unable to store authentication data");
          setLoading(false);
        }
      } else if (req?.type === 'error') {
        // Backend returned an error
        console.error('❌ Backend error:', req?.message);
        toast.error(req?.message || "Invalid access code");
        setLoading(false);
      } else {
        // Unexpected response format
        console.error('❌ Unexpected response format:', req);
        toast.error("Login failed - Invalid response from server");
        setLoading(false);
      }
    } catch (error) {
      console.error('❌ Login exception:', error);
      toast.error(error?.message || "An error occurred during login");
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-4 text-center">
            Enter Your 6-Digit Access Code
          </label>
          <PinInput
            value={accessCode}
            onChange={setAccessCode}
            disabled={loading}
            autoFocus={true}
          />
          <p className="mt-3 text-xs text-gray-500 text-center">
            Enter the 6-digit code provided by NUC.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <button
          type="submit"
          disabled={loading || accessCode.length !== 6}
          className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-4 px-5 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
        >
          {loading && (
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          )}
          <span className="text-white">{loading ? "Signing In..." : "Sign In"}</span>
        </button>

        <div className="text-center text-sm text-gray-600 mt-4">
          <p>Access code provided by NUC for accreditation representatives</p>
        </div>
      </div>
    </form>
  );
};

export default LoginForm;